import React from 'react';
import { Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { useUpgradeDialogStore } from 'store/upgradeDialog';

interface NeedsUpgradeBadgeProps {
  /** The plan feature the test needs (ielts_suite, sat_suite, advanced_question_types). */
  featureKey: string;
  /** Growth-only questions to remove instead of upgrading; null for a test-type gap. */
  questionCount: number | null;
  className?: string;
}

/**
 * Marks an ACTIVE test the org's plan no longer covers: students can't start it.
 * Clicking opens the app-wide upgrade dialog. UX only — the backend refuses the attempt.
 */
export function NeedsUpgradeBadge({ featureKey, questionCount, className }: NeedsUpgradeBadgeProps) {
  const { t } = useTranslation();
  const tooltip = t(
    'test_needs_upgrade_tooltip',
    "Students can't start this test on your current plan. Upgrade, or remove the questions your plan doesn't include.",
  );

  return (
    <Tooltip
      content={tooltip}
      side="top"
      className="w-72 max-w-[calc(100vw-2rem)] whitespace-normal px-3.5 py-2 text-center text-[13px] leading-relaxed"
    >
      <button
        type="button"
        aria-label={tooltip}
        onClick={(event) => {
          // Rows/cards are often links; the badge is its own action.
          event.preventDefault();
          event.stopPropagation();
          useUpgradeDialogStore.getState().open({
            code: 'FEATURE_NOT_ENTITLED',
            featureKey,
            questionCount: questionCount ?? undefined,
          });
        }}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
          'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
          'transition-colors hover:bg-warning-100 dark:hover:bg-warning-900/50',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        <Crown className="size-3" aria-hidden />
        {t('test_needs_upgrade', 'Needs upgrade')}
      </button>
    </Tooltip>
  );
}
